import { FieldValues, useController } from 'react-hook-form';
import { Field, FieldProps as SWFieldProps } from '@spark-web/field';
import { TextInput, TextInputProps } from '@spark-web/text-input';
import type { FormFieldProps } from '../Form';

export type TextInputFieldProps<TFieldValues extends FieldValues = FieldValues> = FormFieldProps<TFieldValues> &
  TextInputProps & { FieldProps?: Omit<SWFieldProps, 'label' | 'children'> } & {
    autoComplete?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    maxLength?: number;
    transformValue?: (value: string) => string;
  };

function TextInputField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  control,
  shouldUnregister,
  FieldProps,
  inputRef,
  maxLength,
  transformValue,
  ...textInputProps
}: TextInputFieldProps<TFieldValues>) {
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules: {
      onBlur: textInputProps?.onBlur,
      onChange: textInputProps?.onChange,
    },
    shouldUnregister,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.type === 'email' ? e.target.value.toLowerCase() : e.target.value;
    if (transformValue) {
      value = transformValue(value);
    }
    if (maxLength && value.length > maxLength) {
      value = value.slice(0, maxLength);
    }
    field.onChange(value);
  };

  return (
    <Field
      label={label}
      {...FieldProps}
      tone={error ? 'critical' : FieldProps?.tone}
      message={error?.message ?? FieldProps?.message}
    >
      <TextInput {...textInputProps} {...field} onChange={handleChange} ref={inputRef} />
    </Field>
  );
}

export default TextInputField;
